import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaLocationDot, FaUser, FaClock, FaCircleCheck, FaBan, FaEnvelope, FaPhone, FaBuilding, FaUserTie, FaTrophy, FaChartLine, FaCalendarCheck, FaCertificate, FaIdBadge, FaXmark, FaFileLines, FaDownload } from 'react-icons/fa6';
import Preloader from './Preloader';

function ApprovedResponders() {
  const [responders, setResponders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [selectedResponder, setSelectedResponder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [responderIncidents, setResponderIncidents] = useState([]);
  const [incidentHistoryLoading, setIncidentHistoryLoading] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Approved Responders";
    fetchResponders();
    getCurrentUser();
    
    // Test database connection and table access
    testDatabaseAccess();
  }, []);

  async function testDatabaseAccess() {
    try {
      console.log('Testing approved_responders table access...');
      const { data, error } = await supabase
        .from('approved_responders')
        .select('id')
        .limit(1);
      
      if (error) {
        console.error('Database access test failed:', error);
      } else {
        console.log('Database access test successful. Table exists and is accessible.');
      }
    } catch (error) {
      console.error('Database connection test failed:', error);
    }
  }

  useEffect(() => {
    if (currentUser) {
      fetchResponders();
    }
  }, [currentUser, search, statusFilter, availabilityFilter]);

  async function getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      setCurrentUser(profile);
    }
  }

  async function fetchResponders() {
    if (!currentUser) return;
    
    setLoading(true);
    setError('');
    
    try {
      let query = supabase
        .from('approved_responders')
        .select('*');

      if (search) {
        query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%,profession.ilike.%${search}%,organization.ilike.%${search}%,responder_id.ilike.%${search}%`);
      }

      if (statusFilter === 'active') {
        query = query.eq('is_active', true);
      } else if (statusFilter === 'inactive') {
        query = query.eq('is_active', false);
      }

      if (availabilityFilter === 'available') {
        query = query.eq('is_available', true);
      } else if (availabilityFilter === 'unavailable') {
        query = query.eq('is_available', false);
      }

      const { data, error } = await query.order('approved_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching responders:', error);
        setError('Failed to load responders');
        setResponders([]);
      } else {
        setResponders(data || []);
      }
    } catch (error) {
      console.error('Error in fetchResponders:', error);
      setError('Failed to load responders');
      setResponders([]);
    } finally {
      setLoading(false);
    }
  }

  async function toggleResponderStatus(responder, field) {
    setActionLoading(true);
    setError(''); // Clear any previous error
    setSuccess(''); // Clear any previous success message
    
    try {
      const newValue = !responder[field];
      console.log('Toggling responder status:', {
        responderId: responder.id,
        field: field,
        currentValue: responder[field],
        newValue: newValue,
        currentUser: currentUser
      });
      
      // Check if user has proper permissions
      if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'super admin')) {
        throw new Error('Insufficient permissions. Only admins can modify responder status.');
      }
      
      // Try multiple RPC functions, then fallback to direct update
      let data, error;
      
      // Attempt 1: Use the simpler RPC function
      try {
        const { data: rpcData, error: rpcError } = await supabase.rpc('admin_update_responder_field', {
          responder_id: responder.id,
          field_name: field,
          new_value: newValue
        });
        
        if (!rpcError) {
          data = [rpcData]; // Wrap in array to match expected format
          error = null;
          console.log('Successfully updated using admin_update_responder_field');
        } else {
          throw rpcError;
        }
      } catch (rpcError1) {
        console.log('First RPC function failed, trying second:', rpcError1.message);
        
        // Attempt 2: Use the original RPC function with admin validation
        try {
          const { data: rpcData, error: rpcError } = await supabase.rpc('update_responder_status', {
            responder_id: responder.id,
            field_name: field,
            new_value: newValue,
            admin_user_id: currentUser.id
          });
          
          if (!rpcError) {
            data = [rpcData]; // Wrap in array to match expected format
            error = null;
            console.log('Successfully updated using update_responder_status');
          } else {
            throw rpcError;
          }
        } catch (rpcError2) {
          console.log('Both RPC functions failed, trying direct update:', rpcError2.message);
          
          // Final fallback to direct update (should work after running the SQL script)
          const updateResult = await supabase
            .from('approved_responders')
            .update({ [field]: newValue })
            .eq('id', responder.id)
            .select();
          
          data = updateResult.data;
          error = updateResult.error;
          
          if (!error) {
            console.log('Successfully updated using direct update');
          }
        }
      }

      if (error) {
        console.error('Database update error:', error);
        // Provide more specific error messages
        if (error.code === '42501') {
          throw new Error(`Database permissions error: The RLS policies need to be updated. Please run the SQL script provided to fix the database permissions.`);
        } else if (error.message?.includes('row-level security policy')) {
          throw new Error(`Row-Level Security policy violation: Please run the database setup script to fix permissions for admin users.`);
        } else if (error.code === '42883') {
          throw new Error(`Database function missing: Please run the SQL script to create the required database functions.`);
        }
        throw new Error(`Update failed: ${error.message || 'Unknown database error'}`);
      }

      console.log('Database update successful:', data);

      // Log audit action (optional - don't let it fail the main operation)
      try {
        await supabase.from('audit_logs').insert([
          {
            user_id: currentUser.id,
            action: `${field === 'is_active' ? (newValue ? 'activated' : 'deactivated') : (newValue ? 'set available' : 'set unavailable')} responder`,
            target_id: responder.id,
            target_type: 'ApprovedResponder',
            details: { 
              responder_id: responder.responder_id,
              responder_name: `${responder.first_name} ${responder.last_name}`,
              field: field,
              new_value: newValue
            }
          }
        ]);
      } catch (auditError) {
        console.warn('Audit log failed (non-critical):', auditError);
      }

      // Show success message
      const actionText = field === 'is_active' 
        ? (newValue ? 'activated' : 'deactivated') 
        : (newValue ? 'set as available' : 'set as unavailable');
      
      const responderName = `${responder.first_name} ${responder.last_name}`;
      setSuccess(`Successfully ${actionText} responder: ${responderName}`);
      setError('');
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
      
      // Refresh the data
      await fetchResponders();
      
    } catch (error) {
      console.error('Error updating responder:', error);
      setError(`Failed to update responder status: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  }

  // Filter responders
  const filteredResponders = useMemo(() => {
    return responders.filter(responder => {
      const matchesSearch = search.trim() === '' ||
        (responder.first_name && responder.first_name.toLowerCase().includes(search.toLowerCase())) ||
        (responder.last_name && responder.last_name.toLowerCase().includes(search.toLowerCase())) ||
        (responder.email && responder.email.toLowerCase().includes(search.toLowerCase())) ||
        (responder.profession && responder.profession.toLowerCase().includes(search.toLowerCase())) ||
        (responder.organization && responder.organization.toLowerCase().includes(search.toLowerCase())) ||
        (responder.responder_id && responder.responder_id.toLowerCase().includes(search.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && responder.is_active) ||
        (statusFilter === 'inactive' && !responder.is_active);
      
      const matchesAvailability = availabilityFilter === 'all' || 
        (availabilityFilter === 'available' && responder.is_available) ||
        (availabilityFilter === 'unavailable' && !responder.is_available);
      
      return matchesSearch && matchesStatus && matchesAvailability;
    });
  }, [responders, search, statusFilter, availabilityFilter]);

  const totalPages = Math.ceil(filteredResponders.length / itemsPerPage);
  const paginatedResponders = useMemo(() => {
    return filteredResponders.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [filteredResponders, page, itemsPerPage]);

  useEffect(() => {
    setPage(1);
    // Clear messages when filters change
    setError('');
    setSuccess('');
  }, [search, statusFilter, availabilityFilter]);

  function formatDate(dateString) {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  function formatResponseTime(minutes) {
    if (!minutes || minutes === 0) return '0 min';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  function handleDocumentPreview(document) {
    console.log('=== DOCUMENT PREVIEW DEBUG ===');
    console.log('Original URL:', document.url);
    console.log('Document type:', document.type);
    console.log('Document name:', document.name);
    
    let formattedUrl = document.url;
    
    // If it's a Supabase storage URL, try to get the proper public URL
    if (formattedUrl.includes('supabase.co')) {
      try {
        // Extract the file path from the URL
        const url = new URL(formattedUrl);
        const pathParts = url.pathname.split('/');
        
        // Find the storage path (everything after /storage/v1/object/public/)
        const storageIndex = pathParts.findIndex(part => part === 'public');
        if (storageIndex !== -1 && storageIndex + 1 < pathParts.length) {
          const bucketName = pathParts[storageIndex + 1];
          const filePath = pathParts.slice(storageIndex + 2).join('/');
          
          console.log('Extracted bucket and path:', { bucketName, filePath });
          
          // Get the proper public URL using Supabase client
          const { data: { publicUrl } } = supabase.storage
            .from(bucketName)
            .getPublicUrl(filePath);
          
          formattedUrl = publicUrl;
          console.log('Generated public URL:', formattedUrl);
        }
      } catch (e) {
        console.error('Error generating public URL:', e);
      }
    }
    
    // Add cache-busting parameter
    if (formattedUrl.includes('supabase.co') && !formattedUrl.includes('?')) {
      formattedUrl += '?t=' + Date.now();
    }
    
    console.log('Final formatted URL:', formattedUrl);
    
    // Test if the URL is accessible
    fetch(formattedUrl, { method: 'HEAD' })
      .then(response => {
        console.log('URL accessibility test:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });
      })
      .catch(error => {
        console.error('URL accessibility error:', error);
      });
    
    setPreviewDocument({
      ...document,
      url: formattedUrl
    });
    setShowDocumentPreview(true);
  }

  function closeDocumentPreview() {
    setShowDocumentPreview(false);
    setPreviewDocument(null);
  }

  async function fetchResponderIncidents(responder) {
    console.log('=== FETCHING RESPONDER INCIDENTS ===');
    console.log('Responder data:', responder);
    console.log('Responder ID:', responder.id);
    console.log('Responder User ID:', responder.user_id);
    
    setIncidentHistoryLoading(true);
    try {
      // First, get incidents where this responder might be involved
      // We'll check multiple sources:
      
      // 1. SOS beacons where this responder was assigned
      const { data: sosIncidents, error: sosError } = await supabase
        .from('sos_beacons')
        .select(`
          id,
          created_at,
          status,
          location,
          user_id,
          resolved_at,
          'SOS Beacon' as incident_type,
          'High' as severity
        `)
        .eq('responder_id', responder.user_id)
        .order('created_at', { ascending: false });

      if (sosError) {
        console.error('Error fetching SOS incidents:', sosError);
      }

      // 2. Check for any incident reports that might be associated with this responder
      // Since there's no direct link, we'll look for incidents in their area or check audit logs
      const { data: auditData, error: auditError } = await supabase
        .from('audit_logs')
        .select(`
          created_at,
          action,
          target_id,
          target_type,
          details
        `)
        .eq('user_id', responder.user_id)
        .eq('target_type', 'IncidentReports')
        .order('created_at', { ascending: false });

      if (auditError) {
        console.error('Error fetching audit incidents:', auditError);
      }

      // 3. For demonstration, we'll also fetch some recent incident reports to show potential assignments
      const { data: recentIncidents, error: incidentError } = await supabase
        .from('incident_reports')
        .select(`
          id,
          type,
          location,
          details,
          severity,
          status,
          created_at
        `)
        .eq('status', 'responding')
        .order('created_at', { ascending: false })
        .limit(5);

      if (incidentError) {
        console.error('Error fetching recent incidents:', incidentError);
      }

      // Combine and format the data
      const combinedIncidents = [];

      // Add SOS incidents
      if (sosIncidents) {
        sosIncidents.forEach(sos => {
          combinedIncidents.push({
            id: `sos-${sos.id}`,
            type: 'SOS Beacon',
            location: sos.location || 'Location not specified',
            severity: 'High',
            status: sos.status,
            created_at: sos.created_at,
            resolved_at: sos.resolved_at,
            source: 'sos_beacon',
            original_id: sos.id
          });
        });
      }

      // Add audit trail incidents (incidents this responder was involved with)
      if (auditData) {
        // Group audit entries by target_id to avoid duplicates
        const incidentIds = [...new Set(auditData.map(audit => audit.target_id))];
        
        for (const incidentId of incidentIds) {
          try {
            const { data: incidentDetail, error: detailError } = await supabase
              .from('incident_reports')
              .select('*')
              .eq('id', incidentId)
              .single();

            if (!detailError && incidentDetail) {
              const relatedAudits = auditData.filter(audit => audit.target_id === incidentId);
              combinedIncidents.push({
                id: `incident-${incidentDetail.id}`,
                type: incidentDetail.type || 'Unknown',
                location: incidentDetail.location,
                severity: incidentDetail.severity,
                status: incidentDetail.status,
                created_at: incidentDetail.created_at,
                source: 'incident_report',
                original_id: incidentDetail.id,
                responder_actions: relatedAudits.map(audit => audit.action).join(', ')
              });
            }
          } catch (error) {
            console.warn('Error fetching incident detail:', error);
          }
        }
      }

      // Sort by date (most recent first)
      combinedIncidents.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      console.log('Combined incidents for responder:', combinedIncidents);
      
      // For testing: Add some sample data if no real incidents found
      if (combinedIncidents.length === 0) {
        console.log('No real incidents found, adding sample data for testing...');
        const sampleIncidents = [
          {
            id: 'sample-1',
            type: 'Sample Test',
            location: 'Test Location - No real incidents found',
            severity: 'Medium',
            status: 'testing',
            created_at: new Date().toISOString(),
            source: 'sample',
            responder_actions: 'This is sample data - no real incidents found'
          }
        ];
        setResponderIncidents(sampleIncidents);
      } else {
        setResponderIncidents(combinedIncidents);
      }

    } catch (error) {
      console.error('Error fetching responder incidents:', error);
      setResponderIncidents([]);
    } finally {
      setIncidentHistoryLoading(false);
    }
  }

  if (loading) return <Preloader text="Loading responders..." />;

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'super admin')) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
            Access denied. You do not have the necessary permissions to view this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <div className="header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Mobile Hamburger Menu */}
                <MobileHamburger
                  isOpen={showMobileMenu}
                  onToggle={() => setShowMobileMenu(prev => !prev)}
                />
                <div className="title-text">Approved Responders</div>
              </div>
            </div>
            
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        <p style={{ color: '#7f8c8d', marginBottom: '24px' }}>
          Manage and monitor emergency responder performance
        </p>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: 16, borderRadius: 8, marginBottom: 24, border: '1px solid #ffcdd2' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div style={{ 
            background: '#e8f5e8', 
            color: '#2e7d32', 
            padding: 16, 
            borderRadius: 8, 
            marginBottom: 24, 
            border: '1px solid #c8e6c9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaCircleCheck style={{ color: '#4caf50' }} />
              <span><strong>Success:</strong> {success}</span>
            </div>
            <button
              onClick={() => setSuccess('')}
              style={{
                background: 'none',
                border: 'none',
                color: '#2e7d32',
                cursor: 'pointer',
                fontSize: 18,
                padding: 4,
                borderRadius: 4,
                opacity: 0.7
              }}
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Statistics Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#2980b9', marginBottom: 4 }}>
              {responders.length}
            </div>
            <div style={{ color: '#7f8c8d', fontSize: 14 }}>Total Responders</div>
          </div>
          <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#27ae60', marginBottom: 4 }}>
              {responders.filter(r => r.is_active).length}
            </div>
            <div style={{ color: '#7f8c8d', fontSize: 14 }}>Active</div>
          </div>
          <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#f39c12', marginBottom: 4 }}>
              {responders.filter(r => r.is_available && r.is_active).length}
            </div>
            <div style={{ color: '#7f8c8d', fontSize: 14 }}>Available</div>
          </div>
          <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: 24, fontWeight: 'bold', color: '#8e44ad', marginBottom: 4 }}>
              {responders.reduce((total, r) => total + (r.total_incidents_responded || 0), 0)}
            </div>
            <div style={{ color: '#7f8c8d', fontSize: 14 }}>Total Responses</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          <div style={{ display: 'flex', flex: 1, gap: 16, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by name, email, ID, profession, or organization"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 2, padding: 12, borderRadius: 8, border: '1px solid #ccc' }}
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ flex: 1, minWidth: 120, padding: 12, borderRadius: 8, border: '1px solid #ccc' }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={availabilityFilter}
              onChange={e => setAvailabilityFilter(e.target.value)}
              style={{ flex: 1, minWidth: 140, padding: 12, borderRadius: 8, border: '1px solid #ccc' }}
            >
              <option value="all">All Availability</option>
              <option value="available">Available</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Responder
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Contact
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Profession
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Performance
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Status
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#2c3e50', borderBottom: '1px solid #e9ecef' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedResponders.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: 40, textAlign: 'center', color: '#7f8c8d' }}>
                      No responders found
                    </td>
                  </tr>
                ) : (
                  paginatedResponders.map(responder => (
                    <tr key={responder.id} style={{ borderBottom: '1px solid #f1f3f4' }}>
                      <td style={{ padding: '16px 20px' }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#2c3e50' }}>
                            {responder.first_name} {responder.last_name}
                          </div>
                          <div style={{ fontSize: 12, color: '#3498db', fontFamily: 'monospace' }}>
                            {responder.responder_id}
                          </div>
                          <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                            {responder.barangay}, {responder.city}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 14, color: '#34495e' }}>
                          {responder.email}
                        </div>
                        <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                          {responder.mobile_number}
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 14, color: '#34495e' }}>
                          {responder.profession}
                        </div>
                        {responder.organization && (
                          <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                            {responder.organization}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                          {responder.experience_years} years exp.
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontSize: 14, color: '#34495e', marginBottom: 4 }}>
                          <FaCircleCheck style={{ color: '#27ae60', marginRight: 4 }} />
                          {responder.total_incidents_responded || 0} responses
                        </div>
                        <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                          <FaClock style={{ marginRight: 4 }} />
                          Avg: {formatResponseTime(responder.average_response_time)}
                        </div>
                        {responder.last_location_update && (
                          <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                            <FaLocationDot style={{ marginRight: 4 }} />
                            Last seen: {formatDate(responder.last_location_update)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                          <span style={{
                            background: responder.is_active ? '#27ae60' : '#95a5a6',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {responder.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span style={{
                            background: responder.is_available ? '#f39c12' : '#e74c3c',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600
                          }}>
                            {responder.is_available ? 'Available' : 'Unavailable'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={async () => {
                              setSelectedResponder(responder);
                              setShowModal(true);
                              // Clear previous data and fetch incident history when modal opens
                              setResponderIncidents([]);
                              setIncidentHistoryLoading(true);
                              try {
                                await fetchResponderIncidents(responder);
                              } catch (error) {
                                console.error('Error fetching responder incidents in button click:', error);
                                setIncidentHistoryLoading(false);
                              }
                            }}
                            style={{
                              background: '#3498db',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 10px',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12
                            }}
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => toggleResponderStatus(responder, 'is_active')}
                            disabled={actionLoading}
                            style={{
                              background: responder.is_active ? '#e74c3c' : '#27ae60',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 10px',
                              borderRadius: 4,
                              cursor: actionLoading ? 'not-allowed' : 'pointer',
                              fontSize: 12,
                              opacity: actionLoading ? 0.6 : 1
                            }}
                            title={responder.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {responder.is_active ? <FaBan /> : <FaCircleCheck />}
                          </button>
                          <button
                            onClick={() => toggleResponderStatus(responder, 'is_available')}
                            disabled={actionLoading || !responder.is_active}
                            style={{
                              background: responder.is_available ? '#f39c12' : '#3498db',
                              color: '#fff',
                              border: 'none',
                              padding: '6px 10px',
                              borderRadius: 4,
                              cursor: (actionLoading || !responder.is_active) ? 'not-allowed' : 'pointer',
                              fontSize: 12,
                              opacity: (actionLoading || !responder.is_active) ? 0.6 : 1
                            }}
                            title={responder.is_available ? 'Set Unavailable' : 'Set Available'}
                          >
                            <FaUser />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid #f1f3f4', display: 'flex', justifyContent: 'center', gap: 8 }}>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e1e5e9',
                  background: page === 1 ? '#f8f9fa' : '#fff',
                  color: page === 1 ? '#6c757d' : '#495057',
                  borderRadius: 6,
                  cursor: page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <span style={{ padding: '8px 16px', color: '#495057' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #e1e5e9',
                  background: page === totalPages ? '#f8f9fa' : '#fff',
                  color: page === totalPages ? '#6c757d' : '#495057',
                  borderRadius: 6,
                  cursor: page === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Simplified Responder Detail Modal */}
        {showModal && selectedResponder && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              width: '92%',
              maxWidth: 1100,
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
              transform: 'scale(1)',
              transition: 'all 0.3s ease'
            }}>
              {/* Enhanced Header */}
              <div style={{
                background: 'linear-gradient(135deg, #08b1c7, #0ea5e9)',
                padding: '24px 28px',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 4px 6px rgba(8, 177, 199, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FaEye style={{ color: '#fff', fontSize: 20 }} />
                  <div>
                    <h2 style={{ 
                      fontWeight: 600, 
                      fontSize: 20, 
                      color: '#fff', 
                      margin: 0
                    }}>
                      {selectedResponder.first_name} {selectedResponder.last_name}
                    </h2>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 16, 
                      marginTop: 6,
                      fontSize: 14
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6,
                        background: 'rgba(255, 255, 255, 0.15)',
                        padding: '4px 8px',
                        borderRadius: 6
                      }}>
                        <FaIdBadge style={{ color: '#fff', fontSize: 12 }} />
                        <span style={{ 
                          fontFamily: 'monospace', 
                          fontWeight: 600,
                          color: '#fff',
                          fontSize: 13
                        }}>
                          {selectedResponder.responder_id}
                        </span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6,
                        color: 'rgba(255, 255, 255, 0.9)'
                      }}>
                        <FaUserTie style={{ fontSize: 12 }} />
                        <span style={{ fontWeight: 500 }}>{selectedResponder.profession}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span
                    style={{
                      background: selectedResponder.is_active 
                        ? '#10b981' 
                        : '#6b7280',
                      color: '#fff',
                      padding: '6px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    title={selectedResponder.is_active ? 'Responder is active' : 'Responder is inactive'}
                  >
                    <FaCircleCheck />
                    {selectedResponder.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span
                    style={{
                      background: selectedResponder.is_available 
                        ? '#08b1c7' 
                        : '#dc2626',
                      color: '#fff',
                      padding: '6px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    title={selectedResponder.is_available ? 'Available for deployment' : 'Not available for deployment'}
                  >
                    <FaUser />
                    {selectedResponder.is_available ? 'Available' : 'Unavailable'}
                  </span>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedResponder(null);
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: 6,
                      width: 32,
                      height: 32,
                      fontSize: 18,
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.3)'}
                    onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
                    aria-label="Close"
                  >×</button>
                </div>
              </div>

              {/* Enhanced Content */}
              <div style={{ 
                padding: '28px',
                overflowY: 'auto',
                maxHeight: 'calc(90vh - 100px)'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
                  
                  {/* Personal Information Card */}
                  <div style={{
                    background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
                    border: '1px solid #e1e8ed',
                    borderRadius: 12,
                    padding: 24,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 16px 0', 
                      color: '#1a1a1a',
                      fontSize: 16,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <FaUser style={{ color: '#08b1c7' }} />
                      Personal Information
                    </h3>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FaEnvelope style={{ color: '#08b1c7', fontSize: 14, minWidth: 16 }} />
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Email</div>
                          <div style={{ color: '#1a1a1a', fontWeight: 500, fontSize: 14 }}>{selectedResponder.email}</div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FaPhone style={{ color: '#08b1c7', fontSize: 14, minWidth: 16 }} />
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Mobile</div>
                          <div style={{ color: '#1a1a1a', fontWeight: 500, fontSize: 14 }}>{selectedResponder.mobile_number}</div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <FaLocationDot style={{ color: '#08b1c7', fontSize: 14, minWidth: 16, marginTop: 2 }} />
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Address</div>
                          <div style={{ color: '#1a1a1a', fontWeight: 500, fontSize: 14, lineHeight: 1.4 }}>
                            {selectedResponder.address}
                            <br />
                            <span style={{ fontSize: 13, color: '#6b7280' }}>
                              {selectedResponder.barangay}, {selectedResponder.city}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Professional Information Card */}
                  <div style={{
                    background: 'linear-gradient(145deg, #ffffff, #f8fafc)',
                    border: '1px solid #e1e8ed',
                    borderRadius: 12,
                    padding: 24,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 16px 0', 
                      color: '#1a1a1a',
                      fontSize: 16,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <FaUserTie style={{ color: '#08b1c7' }} />
                      Professional Info
                    </h3>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FaBuilding style={{ color: '#08b1c7', fontSize: 14, minWidth: 16 }} />
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Organization</div>
                          <div style={{ color: '#1a1a1a', fontWeight: 500, fontSize: 14 }}>
                            {selectedResponder.organization || 'Independent'}
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FaTrophy style={{ color: '#08b1c7', fontSize: 14, minWidth: 16 }} />
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Experience</div>
                          <div style={{ color: '#1a1a1a', fontWeight: 500, fontSize: 14 }}>
                            {selectedResponder.experience_years} years
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <FaCalendarCheck style={{ color: '#08b1c7', fontSize: 14, minWidth: 16 }} />
                        <div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Approved Date</div>
                          <div style={{ color: '#1a1a1a', fontWeight: 500, fontSize: 14 }}>
                            {formatDate(selectedResponder.approved_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Statistics Card */}
                <div style={{
                  background: 'linear-gradient(135deg, #08b1c7, #0ea5e9, #06b6d4)',
                  color: '#fff',
                  borderRadius: 16,
                  padding: 28,
                  marginBottom: 24,
                  boxShadow: '0 8px 25px rgba(8, 177, 199, 0.25)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Decorative background element */}
                  <div style={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    opacity: 0.5
                  }}></div>
                  
                  <h3 style={{ 
                    margin: '0 0 20px 0', 
                    fontSize: 18,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    position: 'relative',
                    zIndex: 1
                  }}>
                    <FaChartLine style={{ fontSize: 16 }} />
                    Performance Statistics
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                        {selectedResponder.total_incidents_responded || 0}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>Total Responses</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                        {formatResponseTime(selectedResponder.average_response_time)}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>Avg Response Time</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
                        {formatResponseTime(selectedResponder.total_response_time_minutes)}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>Total Response Time</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                        {formatDate(selectedResponder.last_location_update)}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>Last Location Update</div>
                    </div>
                  </div>

                  {selectedResponder.current_location_lat && selectedResponder.current_location_lng && (
                    <div style={{ marginTop: 16, textAlign: 'center', paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 4 }}>Current Location</div>
                      <div style={{ 
                        fontFamily: 'monospace', 
                        fontSize: 12, 
                        background: 'rgba(255,255,255,0.2)',
                        padding: '4px 8px',
                        borderRadius: 4,
                        display: 'inline-block'
                      }}>
                        {selectedResponder.current_location_lat.toFixed(6)}, {selectedResponder.current_location_lng.toFixed(6)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Incident History Card */}
                <div style={{
                  background: 'linear-gradient(145deg, #fff5f5, #fef2f2)',
                  border: '1px solid #fed7d7',
                  borderRadius: 12,
                  padding: 24,
                  marginBottom: 24,
                  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.08)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    color: '#1a1a1a',
                    fontSize: 16,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FaChartLine style={{ color: '#ef4444' }} />
                      Incident Response History
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        ({responderIncidents.length} incidents)
                      </span>
                      <button
                        onClick={() => selectedResponder && fetchResponderIncidents(selectedResponder)}
                        style={{
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 10,
                          cursor: 'pointer'
                        }}
                        title="Refresh incident history"
                      >
                        Refresh
                      </button>
                    </div>
                  </h3>
                  
                  {incidentHistoryLoading ? (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      padding: '20px',
                      color: '#6b7280'
                    }}>
                      <div style={{ 
                        width: '20px', 
                        height: '20px', 
                        border: '2px solid #e5e7eb',
                        borderTop: '2px solid #ef4444',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginRight: '8px'
                      }}></div>
                      Loading incident history...
                    </div>
                  ) : responderIncidents.length > 0 ? (
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {responderIncidents.map((incident, index) => (
                        <div key={incident.id} style={{
                          padding: '12px',
                          marginBottom: '8px',
                          background: '#fff',
                          borderRadius: 8,
                          border: '1px solid #e5e7eb',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{
                                  background: incident.type === 'SOS Beacon' ? '#dc2626' : 
                                            incident.type === 'Fire' ? '#ea580c' :
                                            incident.type === 'Medical' ? '#dc2626' :
                                            incident.type === 'Flood' ? '#2563eb' : '#6b7280',
                                  color: '#fff',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600
                                }}>
                                  {incident.type}
                                </span>
                                <span style={{
                                  background: incident.severity === 'High' || incident.severity === 'Critical' ? '#fecaca' : 
                                            incident.severity === 'Medium' ? '#fed7aa' : '#d1fae5',
                                  color: incident.severity === 'High' || incident.severity === 'Critical' ? '#dc2626' : 
                                         incident.severity === 'Medium' ? '#ea580c' : '#065f46',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600
                                }}>
                                  {incident.severity}
                                </span>
                                <span style={{
                                  background: incident.status === 'resolved' ? '#d1fae5' : 
                                            incident.status === 'responding' ? '#dbeafe' : '#f3f4f6',
                                  color: incident.status === 'resolved' ? '#065f46' : 
                                         incident.status === 'responding' ? '#1d4ed8' : '#374151',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600
                                }}>
                                  {incident.status}
                                </span>
                              </div>
                              <div style={{ 
                                fontSize: '13px', 
                                color: '#374151', 
                                fontWeight: 500,
                                marginBottom: '2px'
                              }}>
                                {incident.location}
                              </div>
                              {incident.responder_actions && (
                                <div style={{ 
                                  fontSize: '11px', 
                                  color: '#6b7280',
                                  fontStyle: 'italic'
                                }}>
                                  Actions: {incident.responder_actions}
                                </div>
                              )}
                            </div>
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#9ca3af',
                              textAlign: 'right',
                              minWidth: 'fit-content',
                              marginLeft: '8px'
                            }}>
                              {new Date(incident.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                              <br />
                              {new Date(incident.created_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                          {incident.resolved_at && (
                            <div style={{ 
                              fontSize: '11px', 
                              color: '#059669',
                              fontWeight: 500
                            }}>
                              Resolved: {new Date(incident.resolved_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <div style={{ 
                        textAlign: 'center',
                        color: '#6b7280',
                        padding: '20px',
                        fontStyle: 'italic'
                      }}>
                        No incident response history found for this responder.
                      </div>
                      
                      {/* Debug information */}
                      <div style={{
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: 12,
                        marginTop: 12,
                        fontSize: 12,
                        color: '#6b7280'
                      }}>
                        <div><strong>Debug Info:</strong></div>
                        <div>Loading: {incidentHistoryLoading ? 'Yes' : 'No'}</div>
                        <div>Incidents found: {responderIncidents.length}</div>
                        <div>Responder ID: {selectedResponder?.id}</div>
                        <div>Responder User ID: {selectedResponder?.user_id}</div>
                        <div>Function called: {typeof fetchResponderIncidents}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Certifications Card */}
                {selectedResponder.certifications && (
                  <div style={{
                    background: 'linear-gradient(145deg, #f0f9ff, #e0f2fe)',
                    border: '1px solid #bae6fd',
                    borderRadius: 12,
                    padding: 24,
                    marginBottom: 24,
                    boxShadow: '0 2px 8px rgba(8, 177, 199, 0.08)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 12px 0', 
                      color: '#1a1a1a',
                      fontSize: 16,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <FaCertificate style={{ color: '#08b1c7' }} />
                      Certifications
                    </h3>
                    <div style={{ 
                      padding: 12, 
                      background: '#fff', 
                      borderRadius: 6, 
                      fontSize: 14,
                      color: '#1a1a1a',
                      lineHeight: 1.6,
                      border: '1px solid #e0f2fe'
                    }}>
                      {selectedResponder.certifications}
                    </div>
                  </div>
                )}

                {/* Proof Document Card */}
                {selectedResponder.proof_document_url && (
                  <div style={{
                    background: 'linear-gradient(145deg, #f0f9ff, #e0f2fe)',
                    border: '1px solid #bae6fd',
                    borderRadius: 12,
                    padding: 24,
                    marginBottom: 24,
                    boxShadow: '0 2px 8px rgba(8, 177, 199, 0.08)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 12px 0', 
                      color: '#1a1a1a',
                      fontSize: 16,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <FaFileLines style={{ color: '#08b1c7' }} />
                      Proof Document
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <FaFileLines style={{ color: '#08b1c7' }} />
                      <span style={{ color: '#6b7280' }}>{selectedResponder.proof_document_type || 'Document'}</span>
                      <button
                        onClick={() => handleDocumentPreview({
                          url: selectedResponder.proof_document_url,
                          type: selectedResponder.proof_document_type || 'Document',
                          name: `${selectedResponder.first_name} ${selectedResponder.last_name} - Proof Document`
                        })}
                        style={{
                          background: '#10b981',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 6,
                          border: 'none',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          fontWeight: 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => e.target.style.background = '#059669'}
                        onMouseLeave={e => e.target.style.background = '#10b981'}
                      >
                        <FaEye /> View
                      </button>
                      <a
                        href={selectedResponder.proof_document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: '#08b1c7',
                          color: '#fff',
                          padding: '6px 12px',
                          borderRadius: 6,
                          textDecoration: 'none',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          fontWeight: 500
                        }}
                      >
                        <FaDownload /> Download
                      </a>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 16,
                  paddingTop: 24,
                  borderTop: '2px solid #f1f5f9',
                  marginTop: 8
                }}>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedResponder(null);
                    }}
                    style={{
                      padding: '12px 28px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #08b1c7, #0ea5e9)',
                      color: '#fff',
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(8, 177, 199, 0.3)',
                      letterSpacing: '0.5px'
                    }}
                    onMouseEnter={e => {
                      e.target.style.background = 'linear-gradient(135deg, #0a7a89, #0284c7)';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(8, 177, 199, 0.4)';
                    }}
                    onMouseLeave={e => {
                      e.target.style.background = 'linear-gradient(135deg, #08b1c7, #0ea5e9)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(8, 177, 199, 0.3)';
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Preview Modal */}
        {showDocumentPreview && previewDocument && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              width: '95%',
              maxWidth: 1200,
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              {/* Document Preview Header */}
              <div style={{
                background: '#08b1c7',
                padding: '16px 24px',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e8eaed'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FaFileLines style={{ color: '#fff', fontSize: 18 }} />
                  <h3 style={{ 
                    fontWeight: 600, 
                    fontSize: 16, 
                    color: '#fff', 
                    margin: 0
                  }}>
                    {previewDocument.name}
                  </h3>
                </div>
                <button
                  onClick={closeDocumentPreview}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: 6,
                    width: 32,
                    height: 32,
                    fontSize: 18,
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.2)'}
                  aria-label="Close"
                >
                  <FaXmark />
                </button>
              </div>

              {/* Document Preview Content */}
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '24px',
                background: '#f8fafc'
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: '20px',
                  border: '1px solid #e5e7eb',
                  minHeight: '60vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {previewDocument.url.toLowerCase().includes('.pdf') ? (
                    <iframe
                      src={previewDocument.url}
                      style={{
                        width: '100%',
                        height: '70vh',
                        border: 'none',
                        borderRadius: 6
                      }}
                      title={previewDocument.name}
                      onError={(e) => {
                        console.error('PDF loading error:', e);
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : previewDocument.url.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) ? (
                    <div style={{ width: '100%', textAlign: 'center' }}>
                      <img
                        src={previewDocument.url}
                        alt={previewDocument.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '70vh',
                          objectFit: 'contain',
                          borderRadius: 6
                        }}
                        onError={(e) => {
                          console.error('Image loading error:', e);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div style={{ 
                        display: 'none',
                        textAlign: 'center',
                        color: '#6b7280',
                        padding: '40px'
                      }}>
                        <FaFileLines style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                        <h4 style={{ margin: '0 0 8px 0', color: '#374151' }}>Image Could Not Be Loaded</h4>
                        <p style={{ margin: 0, fontSize: 14 }}>
                          The image could not be loaded. Please try downloading the file instead.
                        </p>
                        <a
                          href={previewDocument.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            background: '#08b1c7',
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: 6,
                            textDecoration: 'none',
                            fontSize: 14,
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: 16,
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => e.target.style.background = '#0a7a89'}
                          onMouseLeave={e => e.target.style.background = '#08b1c7'}
                        >
                          <FaDownload /> Download Document
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: '#6b7280',
                      padding: '40px'
                    }}>
                      <FaFileLines style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                      <h4 style={{ margin: '0 0 8px 0', color: '#374151' }}>Document Preview Not Available</h4>
                      <p style={{ margin: 0, fontSize: 14 }}>
                        This document type cannot be previewed. Please download to view.
                      </p>
                      <a
                        href={previewDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          background: '#08b1c7',
                          color: '#fff',
                          padding: '10px 20px',
                          borderRadius: 6,
                          textDecoration: 'none',
                          fontSize: 14,
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          marginTop: 16,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => e.target.style.background = '#0a7a89'}
                        onMouseLeave={e => e.target.style.background = '#08b1c7'}
                      >
                        <FaDownload /> Download Document
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Document Preview Footer */}
              <div style={{
                background: '#fff',
                padding: '16px 24px',
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                borderTop: '1px solid #e8eaed'
              }}>
                <button
                  onClick={closeDocumentPreview}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    color: '#6b7280',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={e => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  Close
                </button>
                <a
                  href={previewDocument.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: '#08b1c7',
                    color: '#fff',
                    padding: '10px 20px',
                    borderRadius: 6,
                    textDecoration: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={e => e.target.style.background = '#0a7a89'}
                  onMouseLeave={e => e.target.style.background = '#08b1c7'}
                >
                  <FaDownload /> Download
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApprovedResponders;